import type {
  AuditRecord,
  OutreachRecord,
  ProposalRecord,
  LeadRecord,
  LeadSearchRequest,
  LeadSearchResult,
  QaRunRecord,
  RunAuditRequest,
  RunQaRequest,
} from '@360t/shared';

/** In dev, Vite proxies /api to the API server, so a relative base works everywhere. */
const BASE = import.meta.env.VITE_API_URL ? `${import.meta.env.VITE_API_URL}/api` : '/api';

export class ApiError extends Error {
  constructor(public status: number, message: string, public details?: unknown) {
    super(message);
  }
}

/** Fired when any request comes back 401 so the app can show the sign-in screen. */
export const AUTH_EXPIRED_EVENT = 'lh:auth-expired';

export async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...init?.headers },
  });
  if (res.status === 204) return undefined as T;
  if (res.status === 401 && !path.startsWith('/auth/')) {
    window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
  }

  const payload = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new ApiError(res.status, (payload as any).error ?? `Request failed (${res.status})`, (payload as any).details);
  }
  return payload as T;
}

export interface JobAccepted {
  id: string;
  jobId: string;
  status: string;
}

export interface Stats {
  totalLeads: number;
  leadsWithoutWebsite: number;
  hotLeads: number;
  outreachReadyLeads: number;
  leadsWithEmail: number;
  proposalsCreated: number;
  replies: number;
  replyRate: number;
  platforms: { value: string | null; count: number }[];
  pipeline: Record<string, number>;
  auditsRun: number;
  qaScansRun: number;
  reportsSent: number;
  avgOpportunityScore: number;
  avgAuditScore: number;
  avgQaScore: number;
}

export interface LeadListResponse {
  total: number;
  take: number;
  skip: number;
  leads: LeadRecord[];
}

export interface LeadFilters {
  status?: string;
  city?: string;
  category?: string;
  platform?: string;
  hasWebsite?: boolean;
  /** Only leads whose missing website has (or has not) been checked for. */
  websiteChecked?: boolean;
  chain?: boolean;
  hasEmail?: boolean;
  outreachReady?: boolean;
  minScore?: number;
  search?: string;
  /** Pin the list to exactly these leads (serialised comma separated). */
  ids?: string[];
  sort?: 'opportunity' | 'created' | 'name';
  take?: number;
  skip?: number;
}

export interface WebsiteDiscoveryOutcome {
  leadId: string;
  businessName: string;
  websiteUrl: string | null;
  candidatesTried: string[];
  reason: 'found' | 'no-usable-name' | 'nothing-answered' | 'nothing-matched';
}

export interface WebsiteVerifySummary {
  attempted: number;
  websitesFound: number;
  confirmedWithout: number;
  remaining: number;
  outcomes: WebsiteDiscoveryOutcome[];
}

export interface EnrichmentResult {
  leadId: string;
  pagesScanned: string[];
  email: string | null;
  emailConfidence: number | null;
  allEmails: { email: string; source: string; confidence: number }[];
  phones: string[];
  socials: Record<string, string>;
  outreachReady: boolean;
  warnings: string[];
}

export interface DismissedFinding {
  id: string;
  dismissedAsFalsePositive: boolean;
  dismissedAt: string | null;
}

export interface OutreachDraft {
  leadId: string;
  recipient: string | null;
  channel: 'email' | 'phone';
  subject: string;
  body: string;
  basedOn: string[];
  warnings: string[];
}

export interface Facets {
  cities: { value: string; count: number }[];
  categories: { value: string; count: number }[];
  statuses: { value: string; count: number }[];
  platforms?: { value: string | null; count: number }[];
}

export interface SessionUser {
  id: string;
  email: string;
  name: string | null;
}

export const api = {
  me: () => request<SessionUser>('/auth/me'),
  login: (email: string, password: string) =>
    request<SessionUser>('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  logout: () => request<void>('/auth/logout', { method: 'POST' }),
  searchLeads: (body: LeadSearchRequest) =>
    request<LeadSearchResult>('/leads/search', { method: 'POST', body: JSON.stringify(body) }),
  listLeads: (filters: LeadFilters = {}) => {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(filters)) {
      if (v === undefined || v === null || v === '') continue;
      if (Array.isArray(v)) {
        if (v.length > 0) params.set(k, v.join(','));
        continue;
      }
      params.set(k, String(v));
    }
    return request<LeadListResponse>(`/leads?${params.toString()}`);
  },
  leadFacets: () => request<Facets>('/leads/facets'),
  unverifiedCount: () => request<{ unverified: number }>('/leads/unverified-count'),
  verifyWebsites: (limit = 10) =>
    request<WebsiteVerifySummary>('/leads/verify-websites', {
      method: 'POST',
      body: JSON.stringify({ limit }),
    }),
  verifyLeadWebsite: (id: string) =>
    request<WebsiteDiscoveryOutcome>(`/leads/${id}/verify-website`, { method: 'POST', body: '{}' }),
  updateLead: (id: string, patch: Partial<Pick<LeadRecord, 'status' | 'doNotContact' | 'notes' | 'email'>>) =>
    request<LeadRecord>(`/leads/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  deleteLead: (id: string) => request<void>(`/leads/${id}`, { method: 'DELETE' }),
  auditLead: (id: string) => request<JobAccepted>(`/leads/${id}/audit`, { method: 'POST', body: '{}' }),
  enrichLead: (id: string) => request<EnrichmentResult>(`/leads/${id}/enrich`, { method: 'POST', body: '{}' }),
  enrichMissing: (limit = 10) =>
    request<{ attempted: number; enriched: number; phoneOnlyMarked: number; failures: string[] }>(
      '/leads/enrich-missing',
      { method: 'POST', body: JSON.stringify({ limit }) },
    ),
  getLead: (id: string) => request<LeadRecord>(`/leads/${id}`),

  createProposal: (body: { leadId: string; kind?: string; priceOneOff?: number | null; priceMonthly?: number | null }) =>
    request<ProposalRecord>('/proposals', { method: 'POST', body: JSON.stringify(body) }),
  listProposals: (leadId: string) => request<ProposalRecord[]>(`/proposals?leadId=${leadId}`),
  packages: () => request<{ packages: any[]; careTiers: any[] }>('/proposals/packages'),

  outreachDraft: (leadId: string) => request<OutreachDraft>(`/outreach/draft/${leadId}`),
  logOutreach: (body: {
    leadId: string;
    channel?: string;
    subject?: string;
    body?: string;
    recipient?: string;
    proposalId?: string;
    markSent?: boolean;
  }) => request<OutreachRecord>('/outreach', { method: 'POST', body: JSON.stringify(body) }),
  listOutreach: (leadId: string) => request<OutreachRecord[]>(`/outreach?leadId=${leadId}`),
  updateOutreach: (id: string, status: string) =>
    request<OutreachRecord>(`/outreach/${id}`, { method: 'PATCH', body: JSON.stringify({ status }) }),

  qaLead: (id: string) => request<JobAccepted>(`/leads/${id}/qa`, { method: 'POST', body: '{}' }),

  startAudit: (body: RunAuditRequest) =>
    request<JobAccepted>('/audits', { method: 'POST', body: JSON.stringify(body) }),
  getAudit: (id: string) => request<AuditRecord>(`/audits/${id}`),
  /** Flag an audit finding as a false positive. Annotation only — no rescoring. */
  dismissAuditIssue: (auditId: string, issueId: string, dismissedAsFalsePositive: boolean) =>
    request<DismissedFinding>(`/audits/${auditId}/issues/${issueId}`, {
      method: 'PATCH',
      body: JSON.stringify({ dismissedAsFalsePositive }),
    }),
  listAudits: (take = 10) => request<AuditRecord[]>(`/audits?take=${take}`),

  startQa: (body: RunQaRequest) =>
    request<JobAccepted>('/qa-runs', { method: 'POST', body: JSON.stringify(body) }),
  getQaRun: (id: string) => request<QaRunRecord>(`/qa-runs/${id}`),
  /** Flag a QA defect as a false positive. Annotation only — no rescoring. */
  dismissQaDefect: (runId: string, defectId: string, dismissedAsFalsePositive: boolean) =>
    request<DismissedFinding>(`/qa-runs/${runId}/defects/${defectId}`, {
      method: 'PATCH',
      body: JSON.stringify({ dismissedAsFalsePositive }),
    }),
  listQaRuns: (take = 10) => request<QaRunRecord[]>(`/qa-runs?take=${take}`),

  stats: () => request<Stats>('/stats'),
  health: () => request<{ status: string; features: Record<string, boolean> }>('/health'),
};
